import { z } from 'zod';

/**
 * Canonical semantic model for a generated structure.
 *
 * Strict separation: the generator writes this, the renderer reads it.
 * Everything shareable (URL hash, GLB export, persistence) derives from HouseConfig.
 *
 * Coordinate system
 * -----------------
 * Right-handed. +Y is up. +X is "right" when looking at the front facade. +Z runs
 * "out of the page" toward the viewer on the front elevation. The front of the
 * house faces -Z by convention so that the default camera (looking down -Z) sees
 * the front elevation. All dimensions are in meters.
 *
 * Versioning
 * ----------
 * schemaVersion is persisted with every config. Bump on breaking shape changes
 * and add an entry to model/upgrades.ts. Old share links must remain loadable.
 */

export const CURRENT_SCHEMA_VERSION = 9 as const;

/* ----------------------------- enums --------------------------------- */

export const StructureKindSchema = z.enum(['house']);
export type StructureKind = z.infer<typeof StructureKindSchema>;

export const StylePresetSchema = z.enum([
  'modern',
  'suburban',
  'compact',
  'tall',
  'courtyard',
]);
export type StylePreset = z.infer<typeof StylePresetSchema>;

export const RoofTypeSchema = z.enum(['flat', 'gable', 'hip']);
export type RoofType = z.infer<typeof RoofTypeSchema>;

export const RoomTypeSchema = z.enum([
  'bedroom',
  'bathroom',
  'living',
  'kitchen',
  'hall',
  'storage',
  'stairs',
]);
export type RoomType = z.infer<typeof RoomTypeSchema>;

export const FacadeSideSchema = z.enum(['front', 'back', 'left', 'right']);
export type FacadeSide = z.infer<typeof FacadeSideSchema>;

export const AxisSchema = z.enum(['x', 'z']);
export type Axis = z.infer<typeof AxisSchema>;

/* ----------------------------- geometry ------------------------------ */

export const LotConfigSchema = z.object({
  width: z.number().positive(),
  depth: z.number().positive(),
  baseThickness: z.number().positive(),
  setbackFront: z.number().nonnegative(),
  setbackBack: z.number().nonnegative(),
  setbackLeft: z.number().nonnegative(),
  setbackRight: z.number().nonnegative(),
});
export type LotConfig = z.infer<typeof LotConfigSchema>;

export const HouseBodyConfigSchema = z.object({
  footprintWidth: z.number().positive(),
  footprintDepth: z.number().positive(),
  originX: z.number(),
  originZ: z.number(),
  storyHeight: z.number().positive(),
  exteriorWallThickness: z.number().positive(),
  floorThickness: z.number().positive(),
  stylePreset: StylePresetSchema,
  /** Whether the entire body is lifted onto a plinth (slab + steps). */
  plinthHeight: z.number().nonnegative().default(0),
  /** Whether to render front steps from grade up to the plinth. */
  hasFrontSteps: z.boolean().default(false),
});
export type HouseBodyConfig = z.infer<typeof HouseBodyConfigSchema>;

export const WallSegmentConfigSchema = z.object({
  side: FacadeSideSchema,
  start: z.number(),
  end: z.number(),
});
export type WallSegmentConfig = z.infer<typeof WallSegmentConfigSchema>;

export const RoomConfigSchema = z.object({
  id: z.string(),
  type: RoomTypeSchema,
  x: z.number(),
  z: z.number(),
  width: z.number().positive(),
  depth: z.number().positive(),
  wallSegments: z.array(WallSegmentConfigSchema).default([]),
});
export type RoomConfig = z.infer<typeof RoomConfigSchema>;

export const WindowConfigSchema = z.object({
  id: z.string(),
  side: FacadeSideSchema,
  /** Position along the facade measured from the lower-index corner, in meters. */
  offset: z.number().nonnegative(),
  /** Sill height above the story floor, in meters. */
  sillHeight: z.number().nonnegative(),
  width: z.number().positive(),
  height: z.number().positive(),
  size: z.enum(['small', 'medium', 'large']).default('medium'),
});
export type WindowConfig = z.infer<typeof WindowConfigSchema>;

export const DoorConfigSchema = z.object({
  id: z.string(),
  side: FacadeSideSchema,
  offset: z.number().nonnegative(),
  width: z.number().positive(),
  height: z.number().positive(),
  /**
   * `front`/`rear`/`side` — regular exterior door opening.
   * `interior` — reserved (currently unused — partition doors live on
   *              their own model).
   * `garage` — an opening in the exterior wall that leads INTO an attached
   *            garage. Rendered like a regular exterior door (opening,
   *            slab, frame) but the facade on the other side of the door
   *            is the garage interior, not outdoors.
   */
  role: z.enum(['front', 'rear', 'side', 'interior', 'garage']).default('front'),
});
export type DoorConfig = z.infer<typeof DoorConfigSchema>;

/**
 * A small protruding terrace (a "balcony") attached to one facade of a
 * non-ground story. The story's exterior wall in front of the balcony is
 * cut for a door, a thin floor slab projects out by `depth`, and a low
 * railing wraps the three open edges.
 *
 * `width` is along the facade, `depth` extends out from it, `offset` is
 * the position along the facade (lower-index corner = 0).
 */
export const BalconyConfigSchema = z.object({
  side: FacadeSideSchema,
  width: z.number().positive(),
  depth: z.number().positive(),
  offset: z.number().nonnegative(),
  hasRailing: z.boolean().default(true),
  hasDoorToHouse: z.boolean().default(true),
});
export type BalconyConfig = z.infer<typeof BalconyConfigSchema>;

/**
 * An above-grade terrace carved out of one of the upper stories. Unlike
 * a balcony (which projects OUTWARD from a facade) or a deck (which is
 * at grade, next to the house), a terrace is a rectangular CORNER
 * NOTCH cut INTO the house volume from story `level` upward. The
 * story's walls, floor slabs, and (for a top-story terrace) the roof
 * all skip the notch area, leaving a sheltered outdoor room embedded
 * in the house.
 *
 * Corner-only placement
 * ---------------------
 * Terraces always sit at one of the four body-plan corners. This
 * matches real-world residential architecture (a rectangular bite
 * taken out of one corner of the upper floor). The notch therefore
 * has TWO open facade edges — the host facade AND one adjacent facade
 * — and TWO interior-boundary walls that meet at the notch's inner
 * corner and separate the terrace from the rest of the story's rooms.
 *
 * Encoding
 * --------
 * - `level`  story whose FLOOR acts as the terrace deck. `level >= 1`.
 * - `side`   the "host" (primary) facade the terrace attaches to.
 * - `corner` 'origin' = the lower-u corner on that facade, 'far' = the
 *            higher-u corner. Together with `side` this picks one of
 *            the 8 (4 sides × 2 corners) facade-corner combinations.
 *            The ADJACENT cut facade is derived from (`side`, `corner`)
 *            in the renderer / generator.
 * - `width`  notch extent along the host facade, starting at the
 *            corner.
 * - `depth`  notch extent along the adjacent facade, starting at the
 *            same corner.
 *
 * The door always lives on the interior-boundary wall that runs
 * PARALLEL to the host facade (at `depth` meters inward from it);
 * `doorOffsetAlongInterior` is its near-corner offset along that wall
 * measured from the notch's inner corner.
 */
export const TerraceConfigSchema = z.object({
  level: z.number().int().min(1),
  side: FacadeSideSchema,
  corner: z.enum(['origin', 'far']).default('origin'),
  width: z.number().positive(),
  depth: z.number().positive(),
  hasRailing: z.boolean().default(true),
  hasDoorToHouse: z.boolean().default(true),
  doorOffsetAlongInterior: z.number().nonnegative().default(0.4),
  doorWidth: z.number().positive().default(0.9),
});
export type TerraceConfig = z.infer<typeof TerraceConfigSchema>;

export const PartitionAxisSchema = z.enum(['X', 'Z']);
export type PartitionAxis = z.infer<typeof PartitionAxisSchema>;

/**
 * A doorway opening through one interior partition. The partition is
 * identified by the room that "owns" it (each room owns its +X and +Z
 * partition wall), plus the axis. The doorway is centered along the
 * partition unless `tCenter` says otherwise (0..1 along the wall length).
 */
export const PartitionDoorConfigSchema = z.object({
  ownerRoomId: z.string(),
  axis: PartitionAxisSchema,
  tCenter: z.number().min(0).max(1).default(0.5),
  width: z.number().positive().default(0.9),
  height: z.number().positive().default(2.05),
});
export type PartitionDoorConfig = z.infer<typeof PartitionDoorConfigSchema>;

/** A floor-slab cutout. Used for stair openings in the upper-story slab. */
export const SlabHoleConfigSchema = z.object({
  x: z.number(),
  z: z.number(),
  width: z.number().positive(),
  depth: z.number().positive(),
});
export type SlabHoleConfig = z.infer<typeof SlabHoleConfigSchema>;

export const StoryConfigSchema = z.object({
  level: z.number().int().nonnegative(),
  elevation: z.number().nonnegative(),
  rooms: z.array(RoomConfigSchema),
  windows: z.array(WindowConfigSchema),
  doors: z.array(DoorConfigSchema),
  /** Selected interior doorways (subset of all possible partitions). */
  partitionDoors: z.array(PartitionDoorConfigSchema).default([]),
  /** Cutouts in this story's floor slab (e.g. stair well from the floor below). */
  slabHoles: z.array(SlabHoleConfigSchema).default([]),
  balcony: BalconyConfigSchema.optional(),
});
export type StoryConfig = z.infer<typeof StoryConfigSchema>;

/**
 * A ground-level patio deck pinned to one of the four facades.
 *
 * - `side` is the facade the deck attaches to.
 * - `length` is along the facade, `depth` extends out from it.
 * - `offset` is the position along the facade (lower-index corner = 0)
 *   for the near edge of the deck.
 * - `hasStairsToGrade` adds a few descending steps to ground level.
 *   Always false when the body has no plinth (deck is already at grade).
 * - `hasDoorToHouse` reserves a door span on the host facade so the
 *   user can walk in and out. The door is materialised by the openings
 *   generator with `role: 'side'` (or 'rear' / 'front' depending on side).
 */
export const DeckConfigSchema = z.object({
  side: FacadeSideSchema,
  offset: z.number().nonnegative(),
  length: z.number().positive(),
  depth: z.number().positive(),
  hasStairsToGrade: z.boolean().default(false),
  hasDoorToHouse: z.boolean().default(true),
  hasRailing: z.boolean().default(true),
});
export type DeckConfig = z.infer<typeof DeckConfigSchema>;

/**
 * Stair flight descriptor.
 *
 * Levels: 0 is the ground-floor slab top. Positive `toLevel` describes an
 * inter-story stair climbing between two floors. `toLevel = 0` is the
 * SPECIAL CASE used exclusively by the basement access stair: the flight
 * climbs FROM the basement slab top UP TO the ground floor slab top,
 * i.e. its bottom end is below grade.
 */
export const StairConfigSchema = z.object({
  /** The story this stair LANDS at — its lower flight runs from level-1 up.
   *  `0` = basement access stair (lower end sits on the basement floor). */
  toLevel: z.number().int().nonnegative(),
  /** Footprint origin (lower-left of the stair flight) in body-local coords. */
  x: z.number(),
  z: z.number(),
  /** Plan-view dimensions of the stair flight. */
  width: z.number().positive(),
  depth: z.number().positive(),
  /** Direction of climb in body-local space:
   *    'plusZ'  = climbing toward +Z (back of house)
   *    'minusZ' = climbing toward -Z (front)
   *    'plusX'  = climbing toward +X (right)
   *    'minusX' = climbing toward -X (left) */
  climbDir: z.enum(['plusX', 'minusX', 'plusZ', 'minusZ']),
});
export type StairConfig = z.infer<typeof StairConfigSchema>;

/**
 * Optional below-grade level. Basements have no exterior windows (no
 * room for a light well in the generator today) and a single connecting
 * stair up to the ground floor. We model them as a top-level field on
 * `HouseConfig` rather than as a negative-indexed entry in `stories`
 * because their geometry (no openings, thicker walls, floor slab sits
 * below ground plane) and their rendering (hidden unless peeled) are
 * materially different from above-grade stories.
 */
/**
 * Attached garage. Projects outward from one side of the house body and
 * shares a wall with the main body. A garage door hinges on the outer
 * (far-from-house) face, and an interior service door in the shared wall
 * links the garage to the ground floor.
 *
 * Dimensions:
 * - `width` is along the host facade (3.2 m single / ~5.8 m double)
 * - `depth` extends outward from the host facade
 * - `height` is the garage's own ceiling clear height (eaves height)
 *
 * Placement:
 * - `side` is the host facade the garage shares a wall with
 * - `offset` is the position along that facade (lower-corner = 0) of the
 *   shared edge's lower corner, mirroring balcony / deck conventions.
 */
export const GarageConfigSchema = z.object({
  side: FacadeSideSchema,
  offset: z.number().nonnegative(),
  width: z.number().positive(),
  depth: z.number().positive(),
  height: z.number().positive(),
  cars: z.enum(['single', 'double']).default('single'),
  doorType: z.enum(['rollUp', 'swingUp']).default('rollUp'),
  /**
   * Where the garage door faces.
   * - `sideFacing` (legacy): door is on the OUTWARD facade opposite the
   *   shared wall. Used for rear-loading garages or detached layouts.
   * - `frontFacing`: door faces toward `-Z` (the street). The garage is
   *   a wing of the house attached on its left/right side, with its
   *   front face flush with — or slightly recessed from — the house's
   *   front facade. Most American suburban garages are this style.
   */
  orientation: z.enum(['sideFacing', 'frontFacing']).default('sideFacing'),
  /**
   * For `frontFacing` garages: signed distance from the house's front
   * facade to the garage's front face. Positive = garage front is
   * recessed BEHIND the house front; negative = projects in front.
   * Ignored for `sideFacing`.
   */
  frontRecess: z.number().default(0),
  /** Service-door opening into the house (on the host facade). */
  hasInteriorDoor: z.boolean().default(true),
  /** Offset of the interior door along the host facade (inside the shared span). */
  interiorDoorOffset: z.number().nonnegative().default(0),
  interiorDoorWidth: z.number().positive().default(0.9),
});
export type GarageConfig = z.infer<typeof GarageConfigSchema>;

/**
 * A paved approach from one of the lot's fence sides up to the garage
 * door. Stored as an axis-aligned bounding box in WORLD coords plus the
 * fence side it pierces. The renderer just builds a flat slab covering
 * the AABB; the fence builder uses `side` to know which run of fence
 * to slice for the gap.
 *
 * `side` is the fence side the driveway crosses to reach the street /
 * alley. For a front-loaded suburban garage that's `front`; for a
 * side-loaded garage glued to the right facade it's `right`, etc.
 */
export const DrivewayConfigSchema = z.object({
  side: z.enum(['front', 'left', 'right', 'back']).default('front'),
  xLo: z.number(),
  xHi: z.number(),
  zLo: z.number(),
  zHi: z.number(),
  surface: z.enum(['concrete', 'asphalt', 'pavers']).default('concrete'),
});
export type DrivewayConfig = z.infer<typeof DrivewayConfigSchema>;

export const BasementConfigSchema = z.object({
  /** Clear ceiling height of the basement (floor top → ceiling underside). */
  height: z.number().positive(),
  /** Floor slab thickness (poured concrete). */
  floorThickness: z.number().positive(),
  /** Exterior wall thickness (below-grade walls are typically thicker). */
  wallThickness: z.number().positive(),
  /**
   * Basement footprint dimensions. The basement may be LARGER than the
   * house body — historically common with utility/storage basements that
   * extend under a porch or garage. Stored in body-local coords so the
   * renderer can re-align the basement floor and walls without consulting
   * the lot.
   */
  footprintWidth: z.number().positive(),
  footprintDepth: z.number().positive(),
  /** Lower-left corner in body-local coords (where 0,0 = body's lower-left). */
  originX: z.number(),
  originZ: z.number(),
  /** Stair flight that connects the basement floor to the ground floor. */
  stair: StairConfigSchema,
});
export type BasementConfig = z.infer<typeof BasementConfigSchema>;

export const RoofConfigSchema = z.object({
  type: RoofTypeSchema,
  overhang: z.number().nonnegative(),
  height: z.number().nonnegative(),
  direction: AxisSchema.optional(),
});
export type RoofConfig = z.infer<typeof RoofConfigSchema>;

export const FenceStyleSchema = z.enum(['picket', 'slat', 'rail', 'hedge']);
export type FenceStyle = z.infer<typeof FenceStyleSchema>;

export const FenceConfigSchema = z.object({
  height: z.number().positive(),
  inset: z.number().nonnegative(),
  postSpacing: z.number().positive(),
  gateSide: FacadeSideSchema.default('front'),
  gateOffset: z.number().nonnegative(),
  style: FenceStyleSchema.default('rail'),
});
export type FenceConfig = z.infer<typeof FenceConfigSchema>;

export const MailboxConfigSchema = z.object({
  x: z.number(),
  z: z.number(),
  height: z.number().positive(),
  attachedToFence: z.boolean().default(false),
});
export type MailboxConfig = z.infer<typeof MailboxConfigSchema>;

/* ----------------------------- palette & style ----------------------- */

export const PaletteConfigSchema = z.object({
  basePlate: z.string(),
  exteriorWall: z.string(),
  roof: z.string(),
  trim: z.string(),
  fence: z.string(),
  mailbox: z.string(),
  interior: z.string(),
  window: z.string(),
  door: z.string(),
});
export type PaletteConfig = z.infer<typeof PaletteConfigSchema>;

export const StyleConfigSchema = z.object({
  preset: StylePresetSchema,
  trimAccent: z.boolean().default(true),
  symmetricFront: z.boolean().default(false),
  /** All-or-none mullion crosses on every window in this house. */
  windowMullion: z.boolean().default(false),
});
export type StyleConfig = z.infer<typeof StyleConfigSchema>;

/**
 * Texture selections (per-category) chosen by the generator. Each value
 * is a texture-set id matching an entry in `public/textures/manifest.json`
 * for that category, OR omitted to fall back to the kit's flat color.
 *
 * Keeping the choices on the canonical config means the URL hash, GLB
 * userData, and USDA customLayerData all reproduce identically across
 * machines — the textures themselves are static assets, so the same
 * id resolves to the same files anywhere.
 */
export const TextureSelectionsSchema = z.object({
  exteriorWall: z.string().optional(),
  interiorWall: z.string().optional(),
  interiorFloor: z.string().optional(),
  /** Lot-level outdoor ground (lawn) — everything NOT paved (walkway,
   *  driveway each have their own look). Applied only to the base plate. */
  outdoorFloor: z.string().optional(),
  /** Paved hardscape (driveway + walkway). Texture sets live in the
   *  `outdoorFloor` directory alongside lawn variants but occupy their
   *  own slot so the generator can pick grass for the lot and asphalt
   *  for the paths independently. */
  pavement: z.string().optional(),
  roof: z.string().optional(),
  fence: z.string().optional(),
});
export type TextureSelections = z.infer<typeof TextureSelectionsSchema>;

/* ----------------------------- root ---------------------------------- */

export const HouseConfigSchema = z.object({
  schemaVersion: z.literal(CURRENT_SCHEMA_VERSION),
  kind: StructureKindSchema.default('house'),
  seed: z.string().min(1),
  /** Generator score (0..1) of the chosen candidate. Useful for debug UI. */
  score: z.number().min(0).max(1).default(0),
  lot: LotConfigSchema,
  house: HouseBodyConfigSchema,
  stories: z.array(StoryConfigSchema).min(1),
  roof: RoofConfigSchema,
  /** Inter-story stair flights. There is one per pair of stacked stories. */
  stairs: z.array(StairConfigSchema).default([]),
  /** Optional below-grade basement. */
  basement: BasementConfigSchema.optional(),
  /** Optional attached garage. */
  garage: GarageConfigSchema.optional(),
  /** Optional driveway leading to the garage (only present when a garage is). */
  driveway: DrivewayConfigSchema.optional(),
  /** Optional ground-level patio decks attached to one or more facades. */
  decks: z.array(DeckConfigSchema).default([]),
  /** Optional rectangular terraces carved INTO the house footprint from
   *  an upper story up through the roof. See `TerraceConfigSchema`. */
  terraces: z.array(TerraceConfigSchema).default([]),
  fence: FenceConfigSchema.optional(),
  mailbox: MailboxConfigSchema.optional(),
  palette: PaletteConfigSchema,
  style: StyleConfigSchema,
  /** Optional PBR texture selections (see `TextureSelectionsSchema`). */
  textures: TextureSelectionsSchema.optional(),
});
export type HouseConfig = z.infer<typeof HouseConfigSchema>;

export function isHouseConfig(value: unknown): value is HouseConfig {
  return HouseConfigSchema.safeParse(value).success;
}
